import Paper from '@mui/material/Paper';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reducers/store';
import { User } from '../model/user';
import { Group } from '../model/group';
import { useEffect, useState } from 'react';
import { Box, Checkbox, Divider, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography } from '@mui/material';
import { UserRowView } from './userrow';
import { UserInputs } from './userinputs';
import { Loader } from './loader';
import { AlertView } from './alert';
import { AxiosError } from 'axios';
import { getApiAccessToken } from '../auth/auth';
import { useMsal } from '@azure/msal-react';
import { getGroupUsersAsync, getProgramsUsersAsync, sortCurrentGroupUsers, sortCurrentProgramUsers, sortUsers } from '../reducers/userSlice';
import { Program, ProgramUser } from '../model/program';
import { ProgramUserRowView } from './programuserrow';
import { addUsersToProgramAsync } from '../reducers/userSlice';
import { getUserAccess, UserRole } from '../auth/access';
import { AddDialog } from './addconfirm';

export const UserView = () => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const dispatch: AppDispatch = useDispatch();
    
    const programs: Program[] = useSelector((state: RootState) => state.programs.programs);
    const currentProgram: Program | null = useSelector((state: RootState) => state.programs.currentProgram);
    const currentProgramUsers: ProgramUser[] | null = useSelector((state: RootState) => state.users.currentProgramUsers);
    const currentGroupProgramsUsers: ProgramUser[] | null = useSelector((state: RootState) => state.users.currentGroupProgramsUsers);
    const currentGroupUsers: User[] = useSelector((state: RootState) => state.users.currentGroupUsers);
    const users: User[] = useSelector((state: RootState) => state.users.users);
    const currentGroup: Group | null = useSelector((state: RootState) => state.groups.currentGroup);
    const currentClientAdmins: User[] | null = useSelector((state: RootState) => state.users.currentClientAdmins);
    const currentContentEditors: User[] | null = useSelector((state: RootState) => state.users.currentContentEditors);
    
    const [loading, setLoading] = useState<boolean>(false);
    const [newUserAdded, setNewUserLoading] = useState<boolean>(false);
    const [errorMessage, setError] = useState<string | null>(null);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [hideOtherProgramUsers, setHideOtherProgramUsers] = useState<boolean>(false);

    const [ascending, setAscending] = useState(true);

    const [filter, setFilter] = useState('');

    const handleSelectAll = (isChecked: boolean) => {
        if (isChecked) {
            const allUserIds = currentGroup?.displayName === "Public"
                ? users.map(user => user.id)
                : currentGroupUsers?.map(user => user.id);
            setSelectedUsers(allUserIds);
        } else {
            setSelectedUsers([]);
        }
    };

    const handleToggleUserSelection = (userId: string) => {
        setSelectedUsers(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const handleBulkAdd = async () => {
        if (!currentProgram) {
            setError("No program selected");
            return;
        }

        setLoading(true);
        try {
            const accessToken = await getApiAccessToken(instance, activeAccount);
            const selectedUserObjects = currentGroupUsers.filter(user => selectedUsers.includes(user.id));
            await dispatch(addUsersToProgramAsync({ accessToken, users: selectedUserObjects, program: currentProgram }));
            setSelectedUsers([]);
        } catch (error) {
            if (error instanceof AxiosError) {
                setError(error?.message);
            } else if (error instanceof Error) {
                console.error(error);
            }
        }
        setLoading(false);
    };

    // if all users of the client are already part of the program then there is no need to show ability to bulk add
    const allUsersAlreadyAdded = (userArray: User[], programUserArray: ProgramUser[] | null): boolean => {
        if (userArray.length !== programUserArray?.length) {
            return false;
        }

        // Create sets of user IDs
        const set1 = new Set(userArray.map(user => user.id));
        const set2 = new Set(programUserArray.map(user => user.userId));

        // Compare sets
        if (set1.size !== set2.size) {
            return false;
        }

        let equal = Array.from(set1).every((id) => set2.has(id));
        return equal
    };

    const handleFilterOutOtherProgramUsers = async (checked: boolean) => {
        let accessToken: string = await getApiAccessToken(instance, activeAccount)

        setHideOtherProgramUsers(checked)

        if (checked) {
            setLoading(true);
    
            const currentGroupProgramIds = programs.filter(program => program.clientName == currentGroup?.displayName).map(program => program.id)
            dispatch(getProgramsUsersAsync({accessToken, programIds: currentGroupProgramIds}))
    
            setLoading(false);
        }
    }

    const emailFilter = (user: User | ProgramUser) => filter != "" ? user.email.startsWith(filter) : true
    
    const otherProgramUsersFilter = (user: User) => {
        let programUser = currentGroupProgramsUsers?.find(programUser => programUser.userId == user.id)

        // if user is part of another program it will be filtered out
        if (hideOtherProgramUsers && programUser != undefined && programUser.programId != currentProgram?.id) {
            return false
        }

        return true
    }

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        setFilter(value);
    };
    
    const showMakeClientAdmin = getUserAccess(activeAccount)?.role == UserRole.ADMIN
    
    return (
        <div style={{padding: 16}}>
            <AlertView error={errorMessage}/>
            <Typography 
                align='left'>
                Add users
            </Typography>
            <UserInputs 
                group={currentGroup}
            />            
            
            <Typography 
                align='left'>
                Filters
            </Typography>
            <TextField
                label="Filter by email"
                variant="outlined"
                size="medium"
                value={filter}
                onChange={(e) => handleFilterChange(e)}
                sx={{ marginTop: 2, display: "flex", mb: 2, maxWidth: 400, minWidth: 300, marginBottom: 5 }}
            />

            <Divider/>

            <Typography sx={{ marginTop: 2, marginBottom: 1 }} align='left'>{currentProgram?.name} </Typography>
            <Typography sx={{ marginBottom: 2, color: 'gray', fontSize: '0.8em' }} align='left'>{currentProgramUsers?.filter(emailFilter).length ?? 0} users</Typography>

            <TableContainer sx={{ marginBottom: 5 }} component={Paper}>
                <Table aria-label="simple table">
                    <TableHead>
                    <TableRow>
                        <TableCell>
                            <TableSortLabel
                                active={true}
                                direction={ascending ? 'asc' : 'desc'}
                                onClick={() => {
                                    setAscending(!ascending)
                                    dispatch(sortCurrentProgramUsers(ascending))
                                    dispatch(sortCurrentGroupUsers(ascending))
                                    dispatch(sortUsers(ascending))
                                }}
                            >
                                Email
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>Remove from Program</TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            loading ? <Loader width={20} height={20}/> :
                            currentProgramUsers?.filter(emailFilter).map(user => <ProgramUserRowView user={user} program={currentProgram} />)
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography sx={{ marginBottom: 1 }} align='left'>All {currentGroup ? currentGroup.displayName : "client"} users</Typography>
            <Typography 
                sx={{ marginBottom: 2, color: 'gray', fontSize: '0.8em' }} 
                align='left'>{
                    currentGroup?.displayName == "Public" ? 
                        users.filter(emailFilter).filter(otherProgramUsersFilter).length : 
                        currentGroupUsers.filter(emailFilter).filter(otherProgramUsersFilter).length
                } users
            </Typography>
            {
                !allUsersAlreadyAdded(currentGroupUsers, currentProgramUsers) && selectedUsers.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 2 }}>
                        <AddDialog 
                            title={`Add users to ${currentProgram?.name}`}
                            message={`Are you sure you want to add the selected users?`} 
                            onAddConfirmed={() => handleBulkAdd()}
                        />
                    </Box>
                )
            }

            <FormControlLabel
                control={
                    <Checkbox
                        onChange={(event, checked) => handleFilterOutOtherProgramUsers(checked)}
                        inputProps={{ 'aria-label': 'controlled' }}
                        size="small"
                        color="default"
                    />
                }
                label="Hide users that are assigned to other programs"
                sx={{
                    display: 'flex',
                    alignSelf: "flex-start",
                    '& .MuiFormControlLabel-label': {
                        fontSize: '12px',
                        color: '#8b8b8b',
                    },
                }}
            />

            <TableContainer component={Paper}>
                <Table aria-label="simple table">
                    <TableHead>
                    <TableRow>
                        <TableCell>
                        {
                            !allUsersAlreadyAdded(currentGroupUsers, currentProgramUsers) && (
                                <Checkbox
                                    checked={
                                        currentGroup?.displayName === "Public"
                                            ? selectedUsers.length === users.length
                                            : selectedUsers.length === currentGroupUsers.length
                                    }
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                />
                            )
                        }
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={true}
                                direction={ascending ? 'asc' : 'desc'}
                                onClick={() => {
                                    setAscending(!ascending)
                                    dispatch(sortCurrentProgramUsers(ascending))
                                    dispatch(sortCurrentGroupUsers(ascending))
                                    dispatch(sortUsers(ascending))
                                }}
                            >
                                Email
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>Add to Program</TableCell>
                        <TableCell>
                            Verified
                        </TableCell>
                        {
                            showMakeClientAdmin && (
                                <>
                                    <TableCell>Is Admin</TableCell>
                                    <TableCell>Is Content Editor</TableCell>
                                </>
                            )
                        }
                        <TableCell>
                            Delete
                        </TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                    {
                        newUserAdded && (
                            <TableRow sx={{ gap: 1 }}>
                                <TableCell>
                                    <Loader width={20} height={20}/>
                                </TableCell>
                            </TableRow>
                        )
                    }
                    {
                        // The actual 'Public' Group in Azure is more of a placeholder to distinguish it from other groups
                        // and doesn't contain any users. Therefore we bypass showing users of the Public program. 
                        // Since everyone has access to public courses we show all users by default
                        loading ? <Loader width={20} height={20}/> :
                            currentGroup?.displayName == "Public" ? (
                                users.filter(emailFilter).filter(otherProgramUsersFilter).map(user => (
                                    <UserRowView
                                        key={user.id}
                                        programUsers={currentProgramUsers} 
                                        user={user} 
                                        group={currentGroup} 
                                        program={currentProgram}
                                        selected={selectedUsers.includes(user.id)}
                                        toggleSelection={handleToggleUserSelection}
                                        showMakeClientAdmin={showMakeClientAdmin}
                                        isClientAdmin={currentClientAdmins.map(admin => admin.id).includes(user.id)} 
                                        isContentEditor={currentContentEditors.map(editor => editor.id).includes(user.id)} />
                                    )
                                )
                            ):
                            currentGroupUsers?.filter(emailFilter).filter(otherProgramUsersFilter).map(user => (
                                <UserRowView 
                                    key={user.id}
                                    programUsers={currentProgramUsers} 
                                    user={user} 
                                    group={currentGroup} 
                                    program={currentProgram}
                                    selected={selectedUsers.includes(user.id)}
                                    toggleSelection={handleToggleUserSelection}
                                    showMakeClientAdmin={showMakeClientAdmin}
                                    isClientAdmin={currentClientAdmins.map(admin => admin.id).includes(user.id)} 
                                    isContentEditor={currentContentEditors.map(editor => editor.id).includes(user.id)} />
                                )
                            )
                    }
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}